import { useTranslation } from 'react-i18next';
import { FaStar, FaStarHalf } from 'react-icons/fa';
import { ReadOnlyRating } from '../ReadOnlyRating';

interface StarRatingProps {
  rating?: number;
  isEditable?: boolean;
  onChange?: (rating: number) => void;
}

export default function StarRating({ rating, isEditable = false, onChange }: StarRatingProps) {
  const { t } = useTranslation();
  const stars = 10;

  const handleRatingChange = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isEditable || !onChange) return;

    const containerRect = event.currentTarget.getBoundingClientRect();
    let clientX = 0;

    if ('touches' in event && event.touches?.length > 0) {
      clientX = event.touches[0]?.clientX ?? 0;
    } else if ('clientX' in event) {
      clientX = event.clientX;
    } else {
      return;
    }

    const x = clientX - containerRect.left;
    const starWidth = containerRect.width / stars;
    const preciseRating = Math.round((x / starWidth) * 100) / 100;

    // Ensure rating is between 0 and 10
    const clampedRating = Math.max(0, Math.min(10, preciseRating));
    onChange(clampedRating);
  };

  const calculateColor = (index: number) => {
    const ratio = index / stars;
    const redIntensity = Math.round(255 * (1 - ratio) * 2);
    const greenIntensity = Math.round(255 * Math.min(ratio * 2, 1));
    const blueIntensity = Math.round(255 * Math.max(0, (ratio - 0.5) * 2));
    return `rgb(${redIntensity}, ${greenIntensity}, ${blueIntensity})`;
  };

  const renderStars = () => {
    const elements = [];
    const scaledRating = rating ?? 0;

    for (let i = 0; i < stars; i++) {
      const color = calculateColor(i);
      if (i < Math.floor(scaledRating)) {
        elements.push(
          <FaStar
            key={i}
            style={{ color }}
            size={24}
          />
        );
      } else if (i === Math.floor(scaledRating) && scaledRating % 1 > 0) {
        elements.push(
          <FaStarHalf
            key={i}
            style={{ color }}
            size={24}
          />
        );
      } else {
        elements.push(
          <FaStar
            key={i}
            className="text-gray-300"
            size={24}
          />
        );
      }
    }
    return elements;
  };

  return (
    <div className="flex items-center justify-between">
      <div className="relative" role="group" aria-label={t('rating') || ''}>
        <div className="flex">
          {renderStars()}
        </div>
        {isEditable && (
          <div
            className="absolute inset-0 cursor-pointer"
            onMouseMove={handleRatingChange}
            onTouchMove={handleRatingChange}
            onTouchStart={handleRatingChange}
          />
        )}
      </div>
      <ReadOnlyRating rating={rating ?? 0} />
    </div>
  );
}